import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { Button } from './ui/button';

/**
 * Reusable Header Component
 * Sticky header with consistent styling across all pages
 */
export const Header = ({ variant = 'legal', showActions = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);

      // Check which section is currently in view
      const sections = ['features', 'industries', 'pricing', 'faq'];
      const scrollPosition = window.scrollY + 100; // Offset for header height

      for (const sectionId of sections) {
        const section = document.getElementById(sectionId);
        if (section) {
          const { offsetTop, offsetHeight } = section;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
      isScrolled ? 'bg-white shadow-sm pt-0' : 'bg-transparent pt-6'
    }`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-12 py-4">
          {/* Logo + Brand + Navigation closer together */}
          <div className="flex items-center gap-12">
            {/* Logo + Brand */}
            <Link
              to="/"
              aria-label="IDA - Zur Startseite"
              className="flex items-center gap-3"
            >
              <img
                src="/images/IDA_20251107_181046_0000.svg"
                alt="IDA Logo"
                className="h-6 w-auto"
              />
            </Link>

            {/* Navigation - closer to logo */}
            {showActions && (
              <nav className="hidden lg:flex items-center gap-8">
                <a
                  href="#features"
                  className={`font-medium transition-colors pb-1 relative ${
                    activeSection === 'features'
                      ? 'text-teal-700'
                      : 'text-gray-800 hover:text-teal-700'
                  }`}
                >
                  Features
                  {activeSection === 'features' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-700"></span>
                  )}
                </a>
                <a
                  href="#industries"
                  className={`font-medium transition-colors pb-1 relative ${
                    activeSection === 'industries'
                      ? 'text-teal-700'
                      : 'text-gray-800 hover:text-teal-700'
                  }`}
                >
                  Branchen
                  {activeSection === 'industries' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-700"></span>
                  )}
                </a>
                <a
                  href="#pricing"
                  className={`font-medium transition-colors pb-1 relative ${
                    activeSection === 'pricing'
                      ? 'text-teal-700'
                      : 'text-gray-800 hover:text-teal-700'
                  }`}
                >
                  Preise
                  {activeSection === 'pricing' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-700"></span>
                  )}
                </a>
                <a
                  href="#faq"
                  className={`font-medium transition-colors pb-1 relative ${
                    activeSection === 'faq'
                      ? 'text-teal-700'
                      : 'text-gray-800 hover:text-teal-700'
                  }`}
                >
                  FAQ
                  {activeSection === 'faq' && (
                    <span className="absolute bottom-0 left-0 right-0 h-0.5 bg-teal-700"></span>
                  )}
                </a>
              </nav>
            )}
          </div>

          {/* Right side actions - pushed to the right */}
          <div className="flex items-center gap-4 ml-auto">
            {showActions ? (
              // Homepage variant with login and demo buttons
              <>
                <a
                  href={`${process.env.REACT_APP_EU_BACKEND_URL}/login`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-gray-800 hover:text-teal-700 font-medium transition-colors"
                >
                  Login
                </a>
                <Link to="/demo-anfrage">
                  <Button className="bg-teal-700 hover:bg-teal-800">Demo anfragen</Button>
                </Link>
              </>
            ) : (
              // Legal pages variant with back button
              <Link
                to="/"
                className="flex items-center space-x-2 text-gray-600 hover:text-blue-600 transition-colors"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="hidden sm:inline">Zurück zur Startseite</span>
                <span className="sm:hidden">Zurück</span>
              </Link>
            )}
          </div>
        </div>
      </div>
    </header>
  );
};

export default Header;
